import React, { useRef, useState, useEffect } from "react";
import {
  GoogleMap,
  useLoadScript,
  Polygon,
  InfoWindow,
  Marker,
} from "@react-google-maps/api";

import { Button, Select, Spin, Switch, Tooltip } from "antd";

import useGeoData from "./useGeoData";
import useProcessGeoData from "./useProcessGeoData";
import { mapStyles } from "../../../utils/mapStyles";
import useAxiosInstance from "../../../lib/useAxiosInstance";
import { FullscreenOutlined, CloseOutlined } from "@ant-design/icons";
const lib = ["places"];

const AssemblyMap = (props) => {
  const polygonData = props?.polygonData;
  const center = props.center.filter((position) => position !== null);
  const constituencyType = props.storedConstituencyData?.data?.type;
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [showInfoWindow, setShowInfoWindow] = useState(false);

    useEffect(() => {
    if (props.isFullScreen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [props.isFullScreen]);

  const [polyData, setPolyData] = useGeoData(
    constituencyType === "central"
      ? polygonData?.constituencyId
      : polygonData?._id
  );
  const [polygonPaths, setPolygonPaths] = useState([]);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: lib,
  });
  const [compareWithPrevious, setCompareWithPrevious] = useState(false);

  const [selectedYear, setSelectedYear] = useState(null);
  const [yearComparisonData, setYearComparisonData] = useState({});
  const [currentYearData, setCurrentYearData] = useState({});
  const [previousYearData, setPreviousYearData] = useState({});

  const [selectedParty, setSelectedParty] = useState("All");
  const axiosInstance = useAxiosInstance();
  const themes = localStorage.getItem("themes");
  useProcessGeoData(polyData, isLoaded, setPolygonPaths);

  const mapRef = useRef(null);
  let mapStyle;
  if (themes && themes === "darkMode") {
    mapStyle = [
      {
        elementType: "geometry",
        stylers: [{ color: "#1a2332" }],
      },
      {
        elementType: "labels.text.fill",
        stylers: [{ color: "#ffffff" }],
      },
      {
        elementType: "labels.text.stroke",
        stylers: [{ color: "#1a2332" }],
      },

      {
        featureType: "administrative",
        elementType: "geometry.stroke",
        stylers: [{ color: "#4a6fa5" }, { lightness: -10 }],
      },
      {
        featureType: "administrative.land_parcel",
        elementType: "labels.text.fill",
        stylers: [{ color: "#64779f" }],
      },

      {
        featureType: "administrative.province",
        elementType: "geometry.stroke",
        stylers: [{ color: "#4a6fa5" }],
      },
      {
        featureType: "landscape",
        elementType: "geometry",
        stylers: [{ color: "#263238" }],
      },
      {
        featureType: "landscape.man_made",
        elementType: "geometry.stroke",
        stylers: [{ color: "#334e87" }],
      },
      {
        featureType: "landscape.natural",
        elementType: "geometry",
        stylers: [{ color: "#263238" }],
      },
      {
        featureType: "road",
        stylers: [
          {
            visibility: "off",
          },
        ],
      },
      {
        featureType: "road",
        elementType: "labels.icon",
        stylers: [
          {
            visibility: "off",
          },
        ],
      },
      {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
      },
      {
        featureType: "water",
        elementType: "geometry",
        stylers: [{ color: "#0e1626" }],
      },
      {
        featureType: "water",
        elementType: "labels.text.fill",
        stylers: [{ color: "#4e6d70" }],
      },
    ];
  }

  const partyNames = [
    ...new Set(
      center.flatMap((village) => [
        village?.winnerParty?.name,
        village?.runnerUpParty?.name,
        village?.firstRunnerUpParty?.name,
      ])
    ),
  ].filter(Boolean);

  const handleMarkerClick = (position) => {
    props?.setSelectedMarker(position);
    setShowInfoWindow(true);

    if (mapRef.current) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(position);

      mapRef.current.fitBounds(bounds);

      const zoomLevel = 13;
      mapRef.current.setZoom(zoomLevel);
    }
  };

  useEffect(() => {
    if (
      props.isFullScreen &&
      mapRef.current &&
      center.length > 0 &&
      selectedParty !== "All" &&
      props.year
    ) {
      const centerLatLng = new window.google.maps.LatLng(
        center[0].lat,
        center[0].lng
      );
      mapRef.current.panTo(centerLatLng);
      mapRef.current.setZoom(15);
    }
  }, [props.isFullScreen, center, selectedParty, props.year]);

  useEffect(() => {
    if (
      mapRef.current &&
      typeof mapRef.current.fitBounds === "function" &&
      selectedParty === "All"
    ) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(props.selectedMarker);

      mapRef.current.fitBounds(bounds);

      const zoomLevel = 13;
      mapRef.current.setZoom(zoomLevel);
    }
  }, [props.selectedMarker, props.isFullScreen, selectedParty]);

  function lightenColor(hex, percent) {
    if (hex) {
      const num = parseInt(hex.replace("#", ""), 16);
      const r = Math.min(
        255,
        Math.floor((num >> 16) + (255 - (num >> 16)) * percent)
      );
      const g = Math.min(
        255,
        Math.floor(
          ((num >> 8) & 0x00ff) + (255 - ((num >> 8) & 0x00ff)) * percent
        )
      );
      const b = Math.min(
        255,
        Math.floor((num & 0x0000ff) + (255 - (num & 0x0000ff)) * percent)
      );
      return `rgb(${r},${g},${b})`;
    }
  }

  const getVoteShare = (data, partyName) => {
    const match = data?.find((p) => p?.partyName === partyName);
    return match?.voteSharePercentage || null;
  };

  const currShare = getVoteShare(props.villageFetchedData, selectedParty);
  const prevShare = getVoteShare(props.prevVillageFetchedData, selectedParty);

  let fillColor = "#ccc";
  if (currShare != null && prevShare != null) {
    fillColor = currShare > prevShare ? "green" : "red";
  }
  let currentRole = "";
  let previousRole = "";

  const renderMarkers = () => {
    return (
      isLoaded &&
      isMapLoaded &&
      center.filter((village) => {
        if (selectedParty === "All") return true;
        return [
          village?.winnerParty?.name,
          village?.runnerUpParty?.name,
          village?.firstRunnerUpParty?.name,
        ].includes(selectedParty);
      }) &&
      center.map((village, index) => {
        const title = village?.title || "No Name";

        let baseColor = "#ccc"; // Default grey
        let fillColor = baseColor;
        const winner = village?.winnerParty;
        const runnerUp = village?.runnerUpParty;
        const firstRunnerUp = village?.firstRunnerUpParty;

        if (selectedParty === "All") {
          fillColor = winner?.colorCode || "#000";
        } else if (compareWithPrevious) {
          // Comparison logic only if toggle is ON
          const currRole =
            winner?.name === selectedParty
              ? "winner"
              : runnerUp?.name === selectedParty
              ? "runnerUp"
              : firstRunnerUp?.name === selectedParty
              ? "firstRunnerUp"
              : "others";
          currentRole = currRole;
          const prevRole =
            props.prevVillageFetchedData?.find(
              (p) => p?.partyName === selectedParty
            )?.type || "others";
          previousRole = prevRole;

          if (currRole === "winner") {
            fillColor = "green";
          } else if (currRole === "runnerUp" && prevRole === "firstRunnerUp") {
            fillColor = "orange";
          } else if (
            (currRole === "runnerUp" && prevRole === "winner") ||
            (currRole === "firstRunnerUp" &&
              ["winner", "runnerUp"].includes(prevRole))
          ) {
            fillColor = "red";
          } else {
            fillColor = "#999";
          }
        } else {
          // Default visual cue without comparison
          if (winner?.name === selectedParty) {
            fillColor = winner?.colorCode;
          } else if (runnerUp?.name === selectedParty) {
            fillColor = lightenColor(runnerUp?.colorCode, 0.5);
          } else if (firstRunnerUp?.name === selectedParty) {
            fillColor = lightenColor(firstRunnerUp?.colorCode, 0.8);
          } else {
            fillColor = "#ccc";
          }
        }

        // Calculate the width and height of the text
        const textWidth = title.length * 7; // Adjust this multiplier as needed for font size and type
        const textHeight = 20; // Adjust as needed for font size and type

        return (
          <Marker
            key={index}
            position={{
              lat: village.lat,
              lng: village.lng,
            }}
            onClick={() => handleMarkerClick(village)}
            // icon={{
            //   url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
            // <svg xmlns="http://www.w3.org/2000/svg" width="${
            //   textWidth + 10
            // }" height="${textHeight + 20}">
            //   <g>
            //     <rect x="0" y="0" width="${textWidth}" height="${textHeight}" fill="white" stroke="${fillColor}" stroke-width="2" rx="5" ry="5"/>
            //     <polygon points="${textWidth / 2},${textHeight} ${
            //     textWidth / 2 - 5
            //   },${textHeight + 10} ${textWidth / 2 + 5},${
            //     textHeight + 10
            //   }" fill="${fillColor}" stroke="${fillColor}" stroke-width="2"/>
            //     <text x="${textWidth / 2}" y="${
            //     textHeight / 2
            //   }" dominant-baseline="middle" text-anchor="middle" fill="${fillColor}" font-size="12" font-family="Arial">${title}</text>
            //   </g>
            // </svg>`)}`,
            //   scaledSize: new window.google.maps.Size(
            //     textWidth + 10,
            //     textHeight + 20
            //   )
            // }}
            icon={{
              url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="20" viewBox="0 0 30 50">
                    <circle cx="12" cy="12" r="9" fill="${fillColor}" />
                    <rect x="10" y="20" width="4" height="20" fill="#B0D3DF" />
                    </svg>
                `)}`,
              // scaledSize: new window.google.maps.Size(30, 50)
              scaledSize: new window.google.maps.Size(
                textWidth + 10,
                textHeight + 20
              ),
            }}
          />
        );
      })
    );
  };

  const onLoad = (map) => {
    mapRef.current = map;
    setIsMapLoaded(true);
  };

  useEffect(() => {
    if (isLoaded && center.length > 0) {
      onLoad(mapRef.current);
      setShowInfoWindow(!!props.selectedMarker);
    }
  }, [isLoaded, JSON.stringify(center), props.selectedMarker]);

  useEffect(() => {
    if (
      polygonPaths &&
      Array.isArray(polygonPaths) &&
      polygonPaths.length > 0
    ) {
      const bounds = new window.google.maps.LatLngBounds();
      polygonPaths.forEach((polygon) => {
        polygon?.coordinate.forEach((item) => {
          bounds.extend(new window.google.maps.LatLng(item?.lat, item?.lng));
        });
      });
      const polygonCenter = bounds.getCenter();
      mapRef.current.fitBounds(bounds);
      mapRef.current.setCenter(polygonCenter);
    }
  }, [polygonPaths]);

  if (loadError) {
    return <div className="loader">Error loading the map</div>;
  }

  const partyColors = {};
  center.forEach((village) => {
    [
      village?.winnerParty,
      village?.runnerUpParty,
      village?.firstRunnerUpParty,
    ].forEach((party) => {
      if (party?.name && party?.colorCode) {
        partyColors[party.name] = party.colorCode;
      }
    });
  });
  const selectedColor =
    selectedParty !== "All" ? partyColors[selectedParty] : "#000";

  const dark = selectedColor;
  const medium = lightenColor(selectedColor, 0.5);
  const light = lightenColor(selectedColor, 0.7);

  // Function to get the role display text
  const getRoleDisplayText = (role) => {
    switch (role) {
      case "winner":
        return "Winner";
      case "runnerUp":
        return "Runner Up";
      case "firstRunnerUp":
        return "First Runner Up";
      default:
        return "Others";
    }
  };

  if (isLoaded) {
    return (
      <div
        className="map-container transition-all duration-500 ease-in-out"
        style={{
          position: props.isFullScreen ? "fixed" : "relative",
          top: props.isFullScreen ? "0" : "auto",
          left: props.isFullScreen ? "0" : "auto",
          width: props.isFullScreen ? "97vw" : "100%",
          height: props.isFullScreen ? "95vh" : "100%",
          zIndex: props.isFullScreen ? 1000 : "auto",
          overflow: "hidden",
          boxShadow: props.isFullScreen ? "0 0 20px rgba(0,0,0,0.5)" : "none",
          transform: props.isFullScreen ? "translate(2%, 2%)" : "",
        }}
      >
        {props.isFullScreen && (
          <Tooltip title="Close">
            <Button
              icon={<CloseOutlined />}
              size="large"
              shape="circle"
              onClick={() => {
                props.setIsFullScreen(false);
                setSelectedParty("All");
              }}
              className="absolute top-5 right-5 z-50 shadow-md"
            />
          </Tooltip>
        )}

        {props.isFullScreen && (
          <div className="absolute top-5 left-5 z-50 bg-white/10 backdrop-blur-md rounded-lg shadow-lg border border-black/30 p-4">
            <div className="flex flex-col space-y-4">
              <div className="flex items-center">
                <label
                  htmlFor="partyFilter"
                  className="mr-3 font-bold heading-white text-sm min-w-28"
                >
                  Filter by Party:
                </label>
                <Select
                  value={selectedParty}
                  onChange={setSelectedParty}
                  style={{ width: 180 }}
                  size="middle"
                  dropdownStyle={{ zIndex: 9999 }}
                  className="bg-light-dark rounded"
                >
                  <Select.Option value="All">All</Select.Option>
                  {partyNames.map((name) => (
                    <Select.Option key={name} value={name}>
                      {name}
                    </Select.Option>
                  ))}
                </Select>
              </div>

              {props.isFullScreen &&
                selectedParty !== "All" &&
                props.electionYears.data.electionYears.length > 1 && (
                  <div className="flex items-center">
                    <span className="mr-2 font-bold">Compare Performance</span>
                    <Switch
                      checked={compareWithPrevious}
                      onChange={(checked) => setCompareWithPrevious(checked)}
                      size="small"
                      checkedChildren="Yes"
                      unCheckedChildren="No"
                    />
                  </div>
                )}

              <div className="flex items-center">
                <span className="mr-3 heading-white font-bold text-sm min-w-28">
                  Select Year:
                </span>
                <Select
                  value={props.year}
                  onChange={(value) => props.setYear(Number(value))}
                  size="middle"
                  style={{ width: 180 }}
                  dropdownStyle={{ zIndex: 9999 }}
                  className="bg-light-dark rounded"
                >
                  {props.electionYears.data.electionYears.map((year) => (
                    <Select.Option key={year} value={year}>
                      {year}
                    </Select.Option>
                  ))}
                </Select>
              </div>

              {props.electionYears.data.electionYears.length > 1 &&
                compareWithPrevious &&
                selectedParty !== "All" && (
                  <div className="flex items-center">
                    <label className="mr-3 font-bold text-sm min-w-28">
                      Compare with year:
                    </label>
                    <Select
                      value={props.previousYear}
                      onChange={(value) => props.setPreviousYear(Number(value))}
                      size="middle"
                      style={{ width: 180 }}
                      dropdownStyle={{ zIndex: 9999 }}
                      className="bg-white/70 rounded"
                    >
                      {props.electionYears.data.electionYears
                        .filter((year) => year !== props.year)
                        .map((year) => (
                          <Select.Option key={year} value={year}>
                            {year}
                          </Select.Option>
                        ))}
                    </Select>
                  </div>
                )}
            </div>
          </div>
        )}

        {/* Legend box - Position at bottom left */}
        {props.isFullScreen &&
          selectedParty !== "All" &&
          compareWithPrevious && (
            <div className="absolute bottom-5 left-5 z-50 bg-white/20 backdrop-blur-md p-4 rounded-lg shadow-lg border border-white/30">
              <div className="text-sm font-medium mb-2">
                Performance Legend:
              </div>
              <div className="flex items-center space-x-2 mb-1">
                <div className="w-4 h-4 rounded bg-green-500"></div>
                <span>Winner</span>
              </div>
              <div className="flex items-center space-x-2 mb-1">
                <div className="w-4 h-4 rounded bg-orange-400"></div>
                <span>Improved to Runner-Up</span>
              </div>
              <div className="flex items-center space-x-2 mb-1">
                <div className="w-4 h-4 rounded bg-red-500"></div>
                <span>Dropped in Rank</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded bg-gray-400"></div>
                <span>Same</span>
              </div>
            </div>
          )}

        {props.isFullScreen &&
          selectedParty !== "All" &&
          !compareWithPrevious && (
            <div className="absolute bottom-5 left-5 z-50 bg-white/20 backdrop-blur-md p-4 rounded-lg shadow-lg border border-white/30">
              <div className="text-sm font-medium mb-2">Color Legend:</div>
              <div className="flex items-center space-x-2 mb-1">
                <div
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: dark }}
                ></div>
                <span>Winner</span>
              </div>
              <div className="flex items-center space-x-2 mb-1">
                <div
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: medium }}
                ></div>
                <span>Runner-Up</span>
              </div>
              <div className="flex items-center space-x-2 mb-1">
                <div
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: light }}
                ></div>
                <span>2nd Runner-Up</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded bg-gray-400"></div>
                <span>Others</span>
              </div>
            </div>
          )}

        <GoogleMap
          google={props.google}
          mapContainerClassName="map-container"
          options={{
            styles: themes === "darkMode" ? mapStyle : mapStyles,
            disableDefaultUI: true,
          }}
          onLoad={onLoad}
          ref={mapRef}
        >
          {(polygonPaths || []).map((polygon, index) => {
            const bounds = new window.google.maps.LatLngBounds();
            polygon?.coordinate.forEach((point) => {
              const lat = Number(point?.lat);
              const lng = Number(point?.lng);

              if (!isNaN(lat) && !isNaN(lng)) {
                bounds.extend({ lat, lng });
              }
            });
            const center = bounds.getCenter();

            return (
              <React.Fragment key={index}>
                <Polygon
                  paths={polygon?.coordinate
                    ?.map((point) => ({
                      lat: Number(point?.lat),
                      lng: Number(point?.lng),
                    }))
                    .filter((point) => !isNaN(point.lat) && !isNaN(point.lng))}
                  options={{
                    strokeColor: "#810FCB",
                    strokeOpacity: 1.0,
                    strokeWeight: 3.0,
                    fillColor: "#810FCB",
                    fillOpacity: 0.5,
                  }}
                />
              </React.Fragment>
            );
          })}
          {renderMarkers()}
          {showInfoWindow && (
            <InfoWindow
              position={{
                lat: props?.selectedMarker?.lat || 0,
                lng: props?.selectedMarker?.lng || 0,
              }}
              onCloseClick={() => {
                mapRef.current.setZoom(13);
                setShowInfoWindow(false);
              }}
              options={{
                maxWidth: 280,
                pixelOffset: new window.google.maps.Size(0, -5),
              }}
            >
              <div
                style={{
                  padding: "0",
                  fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                  backgroundColor: "#ffffff",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  overflow: "hidden",
                }}
              >
                {/* Header with Village Name */}
                <div
                  style={{
                    backgroundColor: "#f8f9fa",
                    padding: "12px 16px",
                    borderBottom: "1px solid #e9ecef",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "bold",
                      color: "#2c3e50",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {props?.selectedMarker?.title || "Unknown Village"}
                  </div>
                </div>

                {/* Content Area */}
                <div style={{ padding: "16px" }}>
                  {/* Basic Winner Info (Non-fullscreen) */}
                  {!props.isFullScreen && (
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                          marginBottom: "8px",
                        }}
                      >
                        <div
                          style={{
                            width: "12px",
                            height: "12px",
                            borderRadius: "50%",
                            backgroundColor:
                              props?.selectedMarker?.winnerParty?.colorCode ||
                              "#ccc",
                          }}
                        ></div>
                        <span
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#495057",
                          }}
                        >
                          Winner:{" "}
                          {props?.selectedMarker?.winnerParty?.name ||
                            "Unknown"}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Detailed Vote Share (Fullscreen, No Comparison) */}
                  {props.isFullScreen && !compareWithPrevious && (
                    <div>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#6c757d",
                          marginBottom: "12px",
                          textAlign: "center",
                          borderBottom: "1px solid #e9ecef",
                          paddingBottom: "8px",
                        }}
                      >
                        {props.year} Vote Share
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                        }}
                      >
                        {Array.isArray(props?.villageFetchedData) &&
                          props.villageFetchedData
                            .filter(
                              (partyData) => partyData.partyName !== "Total"
                            )
                            .slice(0, 3) // Show top 3 parties
                            .map((partyData, index) => (
                              <div
                                key={index}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  padding: "8px 12px",
                                  backgroundColor:
                                    index === 0 ? "#f8f9fa" : "transparent",
                                  borderRadius: "4px",
                                  border:
                                    index === 0 ? "1px solid #dee2e6" : "none",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: "10px",
                                      height: "10px",
                                      borderRadius: "50%",
                                      backgroundColor:
                                        partyData.partyColorCode || "#ccc",
                                    }}
                                  ></div>
                                  <span
                                    style={{
                                      fontSize: "13px",
                                      fontWeight: index === 0 ? "600" : "500",
                                      color: "#495057",
                                    }}
                                  >
                                    {partyData.partyName}
                                  </span>
                                </div>
                                <span
                                  style={{
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    color: "#007bff",
                                    marginLeft: "1rem",
                                  }}
                                >
                                  {Number(
                                    partyData.voteSharePercentage
                                  ).toFixed(2)}
                                  %
                                </span>
                              </div>
                            ))}
                      </div>
                    </div>
                  )}

                  {/* Year Comparison (Fullscreen with Comparison) */}
                  {props.isFullScreen &&
                    compareWithPrevious &&
                    props.previousYear && (
                      <div>
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#6c757d",
                            marginBottom: "16px",
                            textAlign: "center",
                            borderBottom: "1px solid #e9ecef",
                            paddingBottom: "8px",
                          }}
                        >
                          Vote Share Comparison
                        </div>

                        {/* Current Year */}
                        <div
                          style={{
                            backgroundColor: "#e8f4fd",
                            padding: "12px",
                            borderRadius: "6px",
                            marginBottom: "12px",
                            border: "1px solid #b3d9ff",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "bold",
                                  color: "#0056b3",
                                  marginBottom: "4px",
                                }}
                              >
                                {props.year}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#6c757d",
                                }}
                              >
                                ({getRoleDisplayText(currentRole)})
                              </div>
                            </div>
                            <div
                              style={{
                                fontSize: "20px",
                                fontWeight: "bold",
                                color: "#0056b3",
                              }}
                            >
                              {currShare
                                ? Number(currShare).toFixed(2)
                                : "0.00"}
                              %
                            </div>
                          </div>
                        </div>

                        {/* Previous Year */}
                        <div
                          style={{
                            backgroundColor: "#f8f9fa",
                            padding: "12px",
                            borderRadius: "6px",
                            border: "1px solid #dee2e6",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "bold",
                                  color: "#495057",
                                  marginBottom: "4px",
                                }}
                              >
                                {props.previousYear}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#6c757d",
                                }}
                              >
                                ({getRoleDisplayText(previousRole)})
                              </div>
                            </div>
                            <div
                              style={{
                                fontSize: "20px",
                                fontWeight: "bold",
                                color: "#495057",
                              }}
                            >
                              {prevShare
                                ? Number(prevShare).toFixed(2)
                                : "0.00"}
                              %
                            </div>
                          </div>
                        </div>

                        {/* Change Indicator */}
                        {currShare != null && prevShare != null && (
                          <div
                            style={{
                              marginTop: "12px",
                              textAlign: "center",
                              padding: "8px",
                              borderRadius: "4px",
                              backgroundColor:
                                currShare > prevShare ? "#d4edda" : "#f8d7da",
                              border: `1px solid ${
                                currShare > prevShare ? "#c3e6cb" : "#f5c6cb"
                              }`,
                              color:
                                currShare > prevShare ? "#155724" : "#721c24",
                            }}
                          >
                            <span
                              style={{ fontSize: "12px", fontWeight: "600" }}
                            >
                              {currShare > prevShare ? "↗" : "↘"}
                              {Math.abs(currShare - prevShare).toFixed(2)}%
                              {currShare > prevShare ? "increase" : "decrease"}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                </div>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </div>
    );
  } else {
    return (
      <div className="loader flex flex-col">
        <Spin>
          <div className="mt-12">Loading ...</div>
        </Spin>
      </div>
    );
  }
};

export default AssemblyMap;
